import { AxDataTable } from "@/components/ax/dataTable/AxDataTable";
import { AxMediaPreviewContent } from "@/components/ax/mediaGallery/AxMediaPreviewContent";
import { Button } from "@/components/ui/button";
import { ContentGroup, ContentGroupMember, ContentGroupType } from "@/lib/types";
import { deleteContentGroupMember, getContentGroupMembers, updateContentGroupMembers } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ColumnDef, Row } from "@tanstack/react-table";
import { EditIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AddContentGroupMemberDialog } from "./AddContentGroupMemberDialog";
import { DeleteRowConfirmationDialog } from "@/components/ax/dataTable/DeleteRowConfirmationDialog";
import { showApiError, showSuccess } from "@/lib/toast";
import { Badge } from "@/components/ui/badge";
import { UpdateContentGroupMemberDialog } from "./UpdateContentGroupMemberDialog";
import { LoadingOverlay } from "@/components/LoadingOverlay";

interface Props {
  contentGroup: ContentGroup;
}

export const ContentGroupMembers: React.FC<Props> = ({ contentGroup }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [showAssignContentDialog, setShowAssignContentDialog] = useState(false);
  const [updatingContentGroupMember, setUpdatingContentGroupMember] = useState<ContentGroupMember>();
  const isRobot = contentGroup.type === ContentGroupType.Robot;

  const queryClient = useQueryClient();

  const deleteContentGroupMemberMutation = useMutation({
    mutationFn: (contentGroupMemberGuid: string) =>
      deleteContentGroupMember(String(activeFirm?.firmGuid), contentGroupMemberGuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contentGroupMembers"] });
      showSuccess(t("common.deletedSuccess"));
    },
    onError: showApiError,
  });

  const updateOrdersMutation = useMutation({
    mutationFn: async (newData: ContentGroupMember[]) => {
      const currentData =
        (
          await getContentGroupMembers(
            { numberOfObjectsPerPage: 1000, pageNumber: 1 },
            {
              ContentGroupGuid: contentGroup.contentGroupGuid,
              FirmGuid: activeFirm?.firmGuid,
            }
          )
        ).data.result || [];

      // Create maps for easy lookup
      const currentOrderMap = new Map(currentData.map((item) => [item.contentGroupMemberGuid, Number(item.order)]));

      // Calculate which items actually need updates
      const itemsToUpdate = newData.filter((item, index) => {
        const newOrder = (index + 1) * 100;
        const currentOrder = currentOrderMap.get(item.contentGroupMemberGuid);
        return currentOrder !== newOrder;
      });

      // Only update items that changed position
      const updatePromises = itemsToUpdate.map((member) =>
        updateContentGroupMembers(String(activeFirm?.firmGuid), member.contentGroupMemberGuid, {
          order: (
            (newData.findIndex((item) => item.contentGroupMemberGuid === member.contentGroupMemberGuid) + 1) *
            100
          ).toString(),
          duration: member.duration.toString(),
          isActive: member.isActive.toString(),
          emotion: member.cameraEmotion,
          age: member.cameraAge,
          gender: member.gender,
          categoryId: member.contentGroupMemberCategoryGuid || "",
        })
      );

      return Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contentGroupMembers"],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const robotColumns: ColumnDef<ContentGroupMember>[] = [
    {
      accessorKey: "cameraAgeString",
      header: t("common.age"),
      cell: ({ row }) => <span className="p-2 bg-muted rounded-md">{row.original.cameraAgeString}</span>,
    },
    {
      accessorKey: "cameraEmotionString",
      header: t("common.emotion"),
      cell: ({ row }) => <span className="p-2 bg-muted rounded-md">{row.original.cameraEmotionString}</span>,
    },
    {
      accessorKey: "genderString",
      header: t("user.gender"),
      cell: ({ row }) => <span className="p-2 bg-muted rounded-md">{row.original.genderString}</span>,
    },
    {
      accessorKey: "contentGroupMemberCategoryName",
      header: t("common.categoryName"),
      cell: ({ row }) => <span className="p-2 bg-muted rounded-md">{row.original.contentGroupMemberCategoryName}</span>,
    },
  ];

  const columns: ColumnDef<ContentGroupMember>[] = [
    {
      accessorKey: "name",
      header: t("common.name"),
      cell: ({ row }) => (
        <div className="flex items-center gap-2 ">
          <div className="w-[120px] min-w-[120px]">
            <AxMediaPreviewContent contentGuid={row.original.contentGuid} isDeleting={false} showActions={false} />
          </div>
          <div className="break-all">{row.original.name}</div>
        </div>
      ),
    },
    {
      accessorKey: "duration",
      header: t("contentGroup.duration"),
      cell: ({ row }) => <span className="p-2 bg-muted rounded-md">{row.original.duration}</span>,
    },
    {
      accessorKey: "isActive",
      header: t("contentGroup.isActive"),
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? "default" : "outline"}>
          {row.original.isActive ? t("contentGroup.active") : t("contentGroup.inactive")}
        </Badge>
      ),
    },
    {
      accessorKey: "order",
      header: t("common.order"),
      cell: ({ row }) => <span className="p-2 bg-muted rounded-md">{row.original.order}</span>,
    },
    ...(isRobot ? robotColumns : []),
    {
      accessorKey: "contentGroupMemberGuid",
      header: () => "",
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-end gap-2">
            <DeleteRowConfirmationDialog
              onConfirmDelete={() => deleteContentGroupMemberMutation.mutate(row.original.contentGroupMemberGuid)}
              isLoading={deleteContentGroupMemberMutation.isPending}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setUpdatingContentGroupMember(row.original);
              }}
            >
              <EditIcon className="w-2 h-2" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <LoadingOverlay isLoading={updateOrdersMutation.isPending}>
        <AxDataTable
          numberOfObjectsPerPage={1000}
          hidePageSizeSelector={true}
          columns={columns}
          uniqQueryKey={"contentGroupMembers"}
          fetcher={getContentGroupMembers}
          onReorder={(newData) => {
            updateOrdersMutation.mutate(newData);
          }}
          getRowId={(row) => row.contentGroupMemberGuid}
          dynamicRequestParams={{
            ContentGroupGuid: contentGroup.contentGroupGuid,
            FirmGuid: activeFirm?.firmGuid,
          }}
          extraHeader={
            <Button
              size="sm"
              onClick={() => {
                setShowAssignContentDialog(true);
              }}
            >
              {t("contentGroup.assignContent")} <PlusIcon className="w-4 h-4" />
            </Button>
          }
        />
      </LoadingOverlay>
      {showAssignContentDialog && (
        <AddContentGroupMemberDialog onOpenChange={setShowAssignContentDialog} contentGroup={contentGroup} />
      )}
      {updatingContentGroupMember && (
        <UpdateContentGroupMemberDialog
          contentGroupMember={updatingContentGroupMember}
          onOpenChange={() => setUpdatingContentGroupMember(undefined)}
          showRobotControls={isRobot}
        />
      )}
    </div>
  );
};
